'use client';

import { useState } from 'react';
import { GroupByOption, PaginatedResponse } from '@repo/types';
import { Card } from '@repo/ui';
import { useUpdateWriteOff } from '@xtnd/web/features/ar-collections/queries/writeOffsQuery';
import { DataTable } from '@xtnd/web/shared/components/DataTable';
import { QueryStringParams } from '@xtnd/web/shared';

import { WRITE_OFF_TABLE_DEFAULT_PROPS } from '../../constants';
import {
  DIALOG_MODE_MAP,
  UseWriteOffTabReturn,
  useWriteOffTab,
  WriteOffTabProps,
  useFlattenedWriteOffs,
} from '../../hooks';
import { WriteOff, ArWriteOffFilter } from '../../types/writeOffs';
import ActionDialog from './ActionDialog';
import ClientInvoicesTable from './ClientInvoicesTable';
import { ClientInvoiceToolbar, WriteOffToolbar } from './FilterToolbars';
import { WriteOffTabHeader } from './WriteOffTabHeader';
import { WriteOffActivity } from './WriteOffActivity';

const config = {
  title: 'Approved Write-Offs',
  description: 'Awaiting processing in finance platform',
  emptyState: {
    icon: 'HiClipboardList',
    title: 'No approved write-offs pending processing',
    description: 'There are no approved write-offs found.',
  },
  dialogIds: ['archive', 'note'],
  actions: [
    {
      icon: 'HiArchive',
      iconColor: 'text-blue-600',
      label: 'Mark Processed',
      dialogId: 'archive',
    },
    {
      icon: 'HiChat',
      iconColor: 'text-xtnd-blue',
      label: 'Comment',
      dialogId: 'note',
      showDividerBefore: true,
    },
  ],
  supportsLocalStateUpdate: true,
};

// ============================================================================
// Main Component
// ============================================================================

export default function ApprovedTab({
  isAdmin,
  tableParams,
  setTableParams,
  data,
  isLoading,
  error,
  refetch,
}: WriteOffTabProps) {
  // Activity drawer state
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [selectedActivityRow, setSelectedActivityRow] = useState<WriteOff | null>(null);

  const handleOpenActivity = (row: WriteOff) => {
    setSelectedActivityRow(row);
    setIsActivityOpen(true);
  };

  const tab = useWriteOffTab({
    tabType: 'approved',
    isAdmin,
    tableParams,
    setTableParams,
    dialogIds: [...config.dialogIds],
    actions: [...config.actions],
    supportsLocalStateUpdate: config.supportsLocalStateUpdate,
    onOpenActivity: handleOpenActivity,
  });

  const { mutateAsync: patchWriteOff } = useUpdateWriteOff(
    tableParams,
    isAdmin,
    tab.handleLocalStateUpdate,
    tab.handleLocalCommentUpdate
  );

  const combinedLoading = isLoading;

  const filters = (tableParams.filters as ArWriteOffFilter) ?? {};
  const isDisplayInvoicesTable = !!filters.entityId && filters.groupBy === GroupByOption.CLIENT;

  return (
    <Card className="pt-0 mt-0">
      <div className="p-0 relative">
        {isDisplayInvoicesTable ? (
          <div className="py-4">
            <ClientInvoicesTable
              invoices={data?.data || []}
              clientName={tab.clientName}
              columns={tab.columns[GroupByOption.NONE]}
              onBackToClient={() => {
                const newFilters = { ...filters };
                delete newFilters.entityId;
                setTableParams({ ...tableParams, filters: newFilters, page: 1 });
                tab.setClientName('');
              }}
              isDataTableLoading={isLoading}
              filters={tab.invoiceFilters}
              onFiltersChange={tab.setInvoiceFilters}
              getRowClassName={tab.getRowClassName}
              shouldFlatten={true}
              renderToolbar={() => (
                <ClientInvoiceToolbar
                  isDataTableLoading={isLoading}
                  filters={tab.invoiceFilters}
                  onChange={tab.setInvoiceFilters}
                  showCategoryFilter
                />
              )}
            />
          </div>
        ) : (
          <>
            {/* Header Component */}
            <ApprovedTabHeader tab={tab} isLoading={combinedLoading} onRefresh={refetch} />

            {/* Data Table Component */}
            <ApprovedTabTable
              tab={tab}
              setTableParams={setTableParams}
              tableParams={tableParams}
              data={data}
              isLoading={combinedLoading}
              error={error}
              onRetry={refetch}
            />
          </>
        )}

        {/* Action Dialogs - rendered at top level so they work in both views */}
        {config.dialogIds.map(id => {
          const dialog = tab.dialogs[id];
          return (
            <ActionDialog
              key={id}
              isOpen={dialog?.isOpen ?? false}
              onClose={dialog?.close ?? (() => {})}
              writeOff={dialog?.selectedWriteOff ?? null}
              mode={DIALOG_MODE_MAP[id] ?? 'note'}
              onMutationStateChange={tab.handleMutationStateChange}
              onSubmit={patchWriteOff}
            />
          );
        })}

        {/* Activity Drawer */}
        <WriteOffActivity
          isOpen={isActivityOpen}
          onClose={() => {
            setIsActivityOpen(false);
            setSelectedActivityRow(null);
          }}
          invoiceNumber={selectedActivityRow?.invoiceNumber ?? ''}
          invoiceId={selectedActivityRow?.invoiceId ?? ''}
          tabType={tab.tabType}
        />
      </div>
    </Card>
  );
}

// ============================================================================
// Header Component
// ============================================================================

interface ApprovedTabHeaderProps {
  tab: UseWriteOffTabReturn;
  isLoading: boolean;
  onRefresh: () => void;
}

function ApprovedTabHeader({ tab, isLoading, onRefresh }: Readonly<ApprovedTabHeaderProps>) {
  return (
    <WriteOffTabHeader
      title={config.title}
      description={config.description}
      isLoading={isLoading}
      onRefresh={onRefresh}
      renderToolbar={() => (
        <WriteOffToolbar
          isDataTableLoading={isLoading}
          filters={tab.filters}
          onChange={tab.handleChangeFilters}
        />
      )}
    />
  );
}

// ============================================================================
// Data Table Component
// ============================================================================

interface ApprovedTabTableProps {
  tab: UseWriteOffTabReturn;
  data?: PaginatedResponse<WriteOff>;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  tableParams: QueryStringParams;
  setTableParams: (params: Partial<QueryStringParams>) => void;
}

function ApprovedTabTable({
  tab,
  data,
  tableParams,
  setTableParams,
  isLoading,
  error,
  onRetry,
}: Readonly<ApprovedTabTableProps>) {
  // Flatten data for table display
  const flatData = useFlattenedWriteOffs(data, tab.filters.groupBy);

  return (
    <>
      {/* Data Table */}
      <div className="mt-4">
        <DataTable
          {...WRITE_OFF_TABLE_DEFAULT_PROPS}
          columns={tab.columns[tab.filters.groupBy] ?? []}
          data={{
            data: flatData,
            total: data?.pagination?.total ?? 0,
          }}
          onSort={sortBy => setTableParams({ ...tableParams, sorts: { ...sortBy } })}
          sorting={tableParams.sorts}
          isLoading={isLoading}
          error={error}
          onRetry={onRetry}
          pagination={data?.pagination}
          onPaginationChange={tab.handlePaginationChange}
          emptyState={config.emptyState}
          getRowClassName={tab.getRowClassName}
          onRowClick={tab.handleRowClicked}
          enableColumnResizing
          columnWidthsStorageKey={`writeoffs-approved-table-${tab.filters.groupBy}`}
          // Removed isRowExpandable and renderExpandedRow
        />
      </div>
    </>
  );
}
